import { Column } from "@/components/CellGrid/Column";
import { defaultLabelColumnWidth } from "@/components/CellGrid/LabelColumn";
import { Row } from "@/components/CellGrid/Row";
import { lineSize } from "@/components/CellGrid/Waterfall";
import { Doc } from "@/components/Doc/Doc";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import { loanEpochsAtomsContainer } from "@/financeModels/owasco/epoch";
import { Epoch } from "@/financeModels/owasco/types";
import { BoxComponentProps, Group, Stack, Text } from "@mantine/core";
import { WritableAtom, useAtomValue } from "jotai";
import { memo } from "react";

const { orderedEpochsMolecule } = loanEpochsAtomsContainer;

export const EpochInfoRow = ({ ...props }: BoxComponentProps) => {
  const epochAtoms = useAtomValue(orderedEpochsMolecule);

  return (
    <Row {...props} pl={defaultLabelColumnWidth + lineSize} mb={-6} mt={4}>
      {epochAtoms.map((atom) => (
        <Column key={`${atom}`}>
          <CellContent atom={atom} />
        </Column>
      ))}
    </Row>
  );
};

// For some reason my linter isnt happy moving this into the Cell props?
// So its here....
type CellProps = {
  atom: WritableAtom<Epoch, [Epoch], void>;
};

export const CellContent = memo(
  ({ atom, ...props }: BoxComponentProps & CellProps) => {
    const epoch = useAtomValue(atom);

    const startYear =
      epoch.type === "full"
        ? new Date(epoch.date).getFullYear()
        : String(new Date(epoch.duration?.startDate || 0).getFullYear());
    const endYear =
      epoch.type === "full"
        ? new Date(epoch.date).getFullYear()
        : String(new Date(epoch.duration?.endDate || 0).getFullYear());

    const startMonth =
      epoch.type === "full"
        ? "Jan"
        : new Date(epoch.duration?.startDate || 0).toLocaleString("en-US", {
            month: "short",
          });

    const endMonth =
      epoch.type === "full"
        ? "Dec"
        : new Date(epoch.duration?.endDate || 0).toLocaleString("en-US", {
            month: "short",
          });

    const epochDurationMonthCounts =
      epoch.type === "full" || epoch.type === "trailingTwelveMonths"
        ? 12
        : epoch.duration
          ? (new Date(epoch.duration.endDate).getFullYear() -
              new Date(epoch.duration.startDate).getFullYear()) *
              12 +
            (new Date(epoch.duration.endDate).getMonth() -
              new Date(epoch.duration.startDate).getMonth()) +
            1
          : 0;

    return (
      <Group gap={0} justify="center" align="flex-start" {...props}>
        <Stack gap={0} pos={"relative"} align={"center"}>
          <Text fz={"sm"} fw={500} ta={"center"} lh={1}>
            {startMonth} {startYear} - {endMonth} {endYear}
          </Text>
          {epoch.type === "full" && epoch.annualized ? (
            <LabelExplainer
              showIcon
              preventDefault
              label={
                <Text fz={12} lh={1.4} ta={"center"}>
                  PY Annualized
                </Text>
              }
              explanation={
                <Doc
                  path={`@/docs/pages/model/historicals/annualize-partial-year.mdx`}
                />
              }
            />
          ) : (
            <Text fz={12} lh={1.4} ta={"center"}>
              ({epochDurationMonthCounts} month
              {epochDurationMonthCounts !== 1 ? "s" : ""})
            </Text>
          )}
        </Stack>
      </Group>
    );
  },
);
